import type { CanvasEntity } from '../entityTypes';
import { getConnectorData } from '../entityTypes';

export interface AncestorResult {
  ancestorEntityIds: Set<string>;
  pathConnectorIds: Set<string>;
}

/**
 * BFS backwards from focusEntityId through connector edges.
 * Returns all ancestor entity IDs (excluding waypoints) and connector IDs on the path.
 */
export function computeAncestors(
  entities: CanvasEntity[],
  focusEntityId: string,
): AncestorResult {
  const ancestorEntityIds = new Set<string>();
  const pathConnectorIds = new Set<string>();

  // Reverse adjacency: to.entityId → from.entityId
  // Arrow A→B means "A is prerequisite for B", so to find ancestors of B we follow to→from.
  const reverseAdj = new Map<string, Array<{ fromEntityId: string; connectorId: string }>>();
  for (const e of entities) {
    if (e.kind !== 'connector') continue;
    const cd = getConnectorData(e);
    let arr = reverseAdj.get(cd.to.entityId);
    if (!arr) { arr = []; reverseAdj.set(cd.to.entityId, arr); }
    arr.push({ fromEntityId: cd.from.entityId, connectorId: e.id });
  }

  // Build entity kind map for waypoint detection
  const kindMap = new Map<string, string>();
  for (const e of entities) {
    kindMap.set(e.id, e.kind);
  }

  // BFS backwards
  const queue: string[] = [focusEntityId];
  const visited = new Set<string>();
  visited.add(focusEntityId);

  // Focus node itself is an ancestor (if not waypoint)
  if (kindMap.get(focusEntityId) !== 'waypoint') {
    ancestorEntityIds.add(focusEntityId);
  }

  while (queue.length > 0) {
    const current = queue.shift()!;
    const edges = reverseAdj.get(current);
    if (!edges) continue;

    for (const { fromEntityId, connectorId } of edges) {
      pathConnectorIds.add(connectorId);

      if (!visited.has(fromEntityId)) {
        visited.add(fromEntityId);
        queue.push(fromEntityId);

        if (kindMap.get(fromEntityId) !== 'waypoint') {
          ancestorEntityIds.add(fromEntityId);
        }
      }
    }
  }

  return { ancestorEntityIds, pathConnectorIds };
}
